import { NextPage, NextPageContext } from 'next';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ErrorPage from '@/components/route/error/ErrorPage/ErrorPage';
import AllReviews from '@/components/route/listing/AllReviews/AllReviews';
import AllReviewsHeader from '@/components/route/listing/AllReviewsHeader/AllReviewsHeader';
import Pagination from '@/components/route/rvDealerships/Pagination';
import HeadMeta from '@/components/static-pages/HeadMeta';
import { REVIEWS } from '@/constants/urls';
import { useRouter } from '@/hooks/useRouter';
import { fetchReviews } from '@/redux/modules/owner/actions';
import {
  ownerInfoSelector,
  ownerReviewsCountSelector,
  ownerReviewsSelector,
} from '@/redux/modules/owner/selectors';
import { IStore } from '@/utility/redux/store';
import { handleReviewLighboxChange } from '@/utility/reviews';
import { createOwnerUrlObj } from '@/utility/routes';

interface IError {
  statusCode: number;
  message?: string;
}

interface IProps {
  error?: IError | null;
}

const pageLimit = 16;

const ReviewsPage: NextPage<IProps> = ({ error }) => {
  const router = useRouter();
  const {
    query: { slug, page = 1 },
  } = router;
  const dispatch = useDispatch();

  const formattedReviews = useSelector(ownerReviewsSelector);
  const ownerInfo = useSelector(ownerInfoSelector);
  const totalReviews = useSelector(ownerReviewsCountSelector);
  const totalPages = Math.ceil(totalReviews / pageLimit);
  const intl = useIntl();

  const onChangePage = (newPage: number) => {
    dispatch(fetchReviews(slug as string, newPage));
    router.replace(
      createOwnerUrlObj({ slug: router.query.slug }, slug => `/pro/${slug}${REVIEWS}/${newPage}`),
      undefined,
      { shallow: true },
    );
    typeof window === 'object' && window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error) {
    return <ErrorPage statusCode={error.statusCode} message={error.message} />;
  }

  return (
    <>
      <HeadMeta
        title={intl.formatMessage(
          {
            defaultMessage: '{bussinessName} Reviews',
            id: '1OOs45',
          },
          { bussinessName: ownerInfo?.business_name || '' },
        )}
        noindex
      />
      <main className="bg-white">
        <div className="sticky top-0 z-10 w-full bg-white">
          <AllReviewsHeader
            className="grid grid-cols-main container-main gap-x-default"
            title={ownerInfo?.business_name}
            href={`/pro/${slug}`}
          />
        </div>
        <div className="grid grid-cols-main container-main gap-x-default mt-9 md:mt-14">
          <AllReviews
            totalReviews={totalReviews}
            averageRating={ownerInfo?.owner_score || 0}
            reviews={formattedReviews}
            onLightboxChange={handleReviewLighboxChange}
          />
        </div>
        <div className="mt-5 mb-32">
          <Pagination currentPage={Number(page)} totalPages={totalPages} onClick={onChangePage} />
        </div>
      </main>
    </>
  );
};

ReviewsPage.getInitialProps = async ({
  res,
  query,
  reduxStore,
}: NextPageContext & { reduxStore: IStore }) => {
  const { slug, page = 1 } = query;

  if (typeof slug !== 'string' || !slug) {
    if (res) res.statusCode = 404;
    return { error: { statusCode: 404 } };
  }

  await reduxStore.dispatch(fetchReviews(slug, Number(page)));

  return {};
};

export default ReviewsPage;
